import { useState, useEffect } from 'react';
import { fetchProjectConfig, updateProjectConfig } from '@/lib/api';
import { useConfig } from '@/lib/ConfigContext';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Settings, Plus, Trash2, Check, Building2 } from 'lucide-react';

export default function Configuracion() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nombre, setNombre] = useState('');
  const [modelos, setModelos] = useState([]);
  const [nuevoModelo, setNuevoModelo] = useState('');
  const [saving, setSaving] = useState(false);
  const { setConfig: setGlobalConfig } = useConfig() || {};

  useEffect(() => {
    fetchProjectConfig()
      .then(c => { setConfig(c); setNombre(c.proyecto_nombre); setModelos(c.modelos_vivienda || []); })
      .catch(() => toast.error('Error al cargar configuración'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateProjectConfig({ proyecto_nombre: nombre, modelos_vivienda: modelos });
      setConfig(updated);
      if (setGlobalConfig) setGlobalConfig(updated);
      toast.success('Configuración guardada');
    } catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const addModelo = () => {
    if (nuevoModelo.trim() && !modelos.includes(nuevoModelo.trim())) {
      setModelos(m => [...m, nuevoModelo.trim()]);
      setNuevoModelo('');
    }
  };

  const removeModelo = (m) => setModelos(ms => ms.filter(x => x !== m));

  if (loading) return (<div className="space-y-6"><Skeleton className="h-10 w-48" /><Skeleton className="h-60 rounded-xl" /></div>);

  return (
    <div className="space-y-6" data-testid="config-page">
      <PageHeader title="Configuración" subtitle="Ajustes del proyecto y opciones generales" />

      <Card className="rounded-xl border bg-white">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Building2 className="h-4 w-4 text-[#F05A28]" /> Datos del Proyecto
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Nombre del Desarrollo</label>
            <Input value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Residencial Los Álamos" data-testid="config-nombre" />
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-xl border bg-white">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Settings className="h-4 w-4 text-[#F05A28]" /> Modelos de Vivienda
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {modelos.map(m => (
              <Badge key={m} variant="outline" className="bg-slate-50 text-slate-700 border-slate-200 text-sm px-3 py-1.5 flex items-center gap-2">
                {m}
                <button onClick={() => removeModelo(m)} className="text-slate-400 hover:text-red-500 transition-colors"><Trash2 className="h-3 w-3" /></button>
              </Badge>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              value={nuevoModelo}
              onChange={e => setNuevoModelo(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addModelo(); }}
              placeholder="Nuevo modelo (ej: Casa Diamante)"
              className="flex-1"
              data-testid="config-nuevo-modelo"
            />
            <Button variant="outline" onClick={addModelo} disabled={!nuevoModelo.trim()}>
              <Plus className="h-4 w-4 mr-1" /> Agregar
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} data-testid="config-save">
          <Check className="h-4 w-4 mr-1.5" /> {saving ? 'Guardando...' : 'Guardar Configuración'}
        </Button>
      </div>
    </div>
  );
}
